import {gradientAtPositionSelector} from "./GradientAtPositionSelector.js";
import {usePageMetaStore} from "../stores/PageMetaStore.js";
import {getCurrentEducation, getMostRecentJob} from "./CandidateFunctions.js";
import {EnvelopeOpenIcon as EnvelopeOpenIconOutline} from "@heroicons/vue/24/outline/index.js";
import {
  BuildingOfficeIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  EyeIcon,
  InformationCircleIcon,
  UserPlusIcon,
  PencilIcon,
  Cog6ToothIcon,
  PhoneIcon,
  StarIcon,
  VideoCameraIcon
} from "@heroicons/vue/20/solid/index.js";
import {LOCATION_IN_PERSON, LOCATION_PHONE, STATUS_CANCELLED, STATUS_COMPLETED} from "../constants/Interviews.js";
import {differenceBetweenDates, formatDateLong} from "./FormatDates.js";
import pluralize from "pluralize-esm";
import {ENV_DEMO, ENV_STAGING} from "../constants/EnvironmentType";
import {
  BACKGROUND_CHECK_STATUS_ERROR,
  BACKGROUND_CHECK_STATUS_COMPLETED_GOOD,
  BACKGROUND_CHECK_STATUS_IN_PROGRESS
} from "../constants/BackgroundChecks.js";
import {PaperAirplaneIcon} from "@heroicons/vue/24/solid/index.js";

export const teamMemberSummary = (teamMembers) => {
  if (!teamMembers || !teamMembers.length) return "(Needs Assignment)";
  if (teamMembers.length === 1) return teamMembers[0].firstName;
  return (
    teamMembers[0].firstName +
    " & " +
    (teamMembers.length - 1) +
    " other" +
    (teamMembers.length > 2 ? "s" : "")
  );
};

export const teamMemberSummaryAllNames = (teamMembers) => {
  if (!teamMembers || !teamMembers.length) return "(Needs Assignment)";
  let names = teamMembers
    .map((teamMember) => `${teamMember.firstName}`);

  let formattedNames;

  if (names.length > 1) {
    let last = names.pop();
    formattedNames = `${names.join(", ")} & ${last}`;
  } else formattedNames = names[0];

  return formattedNames;
};

export const getColorAtIndex = (index, total, opacity) => {
  let color = gradientAtPositionSelector(index, Math.max(total - 1, 1));
  return `rgba(${color.red},${color.green},${color.blue},${opacity})`;
};

export const compareInterviewSettings = (a, b) => {
  const orderDifference = Number(a?.order ?? 0) - Number(b?.order ?? 0);
  if (orderDifference !== 0) return orderDifference;

  return Number(a?.id ?? 0) - Number(b?.id ?? 0);
};

export const handleMapOpportunity = (state, opportunity) => {
  const pageMetaStore = usePageMetaStore();

  state.title = opportunity.title;
  state.shortCode = opportunity.shortCode;
  pageMetaStore.pageTitle = opportunity.title;
  state.city = opportunity.city;
  state.state = opportunity.state;
  state.uuid = opportunity.uuid;
  state.status = opportunity.status;
  state.internalNotes = opportunity.internalNotes;
  state.teamMembers = opportunity.teamMembers;
  state.backgroundCheckRequired = opportunity.backgroundCheck;
  state.showOfferLetters = opportunity.showOfferLetters;
  state.hiresSoFar = opportunity.hires?.soFar ?? 0;
  state.hiresNeeded = opportunity.hires?.needed ?? 0;
  state.hiringManagers = opportunity.hiringManagers;
  state.screeningQuestions = opportunity.screeningQuestions;
  state.company = {
    avatar: opportunity.company.avatar,
    logo: opportunity.company.logo,
    name: opportunity.company.name,
    nickname: opportunity.company.nickname,
    primaryForeground: opportunity.company.primaryForeground,
    primaryBackground: opportunity.company.primaryBackground,
    secondaryForeground: opportunity.company.secondaryForeground,
    secondaryBackground: opportunity.company.secondaryBackground,
  };

  opportunity.interviewSettings.sort(compareInterviewSettings);

  state.steps.connect = opportunity.interviewSettings.map((interviewSetting) => {
    return {
      ...interviewSetting,
      candidates: [],
    };
  });

  if (!state.backgroundCheckRequired) {
    const bgIndex = state.steps.hire.findIndex((step) => step.name === "Background Check");
    if (bgIndex !== -1) state.steps.hire.splice(bgIndex, 1);
  }

  if (!state.showOfferLetters) {
    const offerIndex = state.steps.hire.findIndex((step) => step.name === "Offer Letter");
    if (offerIndex !== -1) state.steps.hire.splice(offerIndex, 1);
  }
};

export const handleMapApplications = (state, applications) => {
  for (let i = 0; i < applications.length; i++) {
    let application = applications[i];
    assignNextStep(application, state, true);
  }
};

/**
 * Resolve which interview column an application belongs in.
 *
 * The column is the position of the furthest live (non-cancelled) interview's setting within the
 * ordered interview steps — the same ordinal the backend uses in
 * CompanyApplicationMoveService::getApplicationProgress(). A count of interviews cannot be used as
 * the position: a cancelled interview at an earlier step makes the count trail the true ordinal, so
 * the card would render a column too far left and its next step would name the wrong destination.
 *
 * Interviews whose setting is missing from the ordered steps (deleted or belonging to another
 * opportunity) are ignored, mirroring the backend's interviewsWhereHasSettings() constraint.
 *
 * @param {Object} application Application with a camel-cased `interviews` collection.
 * @param {Array} connectSteps Ordered interview steps, each carrying the interview setting `id`.
 * @returns {number} Zero-based interview column index, or -1 when no live interview places the card.
 */
export const resolveInterviewStepIndex = (application, connectSteps) => {
  const steps = connectSteps ?? [];

  if (!Array.isArray(application?.interviews)) {
    return application?.totalInterviews ? application.totalInterviews - 1 : -1;
  }

  return application.interviews.reduce((furthestIndex, interview) => {
    if (interview?.status === STATUS_CANCELLED) return furthestIndex;

    const stepIndex = steps.findIndex((step) => Number(step?.id) === Number(interview?.interviewSettingId));

    return stepIndex > furthestIndex ? stepIndex : furthestIndex;
  }, -1);
};

/**
 * Find the interview that represents an application's state at a given interview step.
 *
 * An interview setting can hold more than one interview row — cancelling and rescheduling leaves
 * the cancelled attempt behind — so matching on interview setting id alone is not one-to-one. The
 * live (non-cancelled) attempt is the one a card should describe; the most recently created row
 * wins when several qualify, and a cancelled row is only used when nothing live remains.
 *
 * @param {Object} candidate Application card with a camel-cased `interviews` collection.
 * @param {number|string} interviewSettingId Interview setting whose interview is wanted.
 * @returns {Object|undefined} The interview describing this step, or undefined when there is none.
 */
export const findStepInterview = (candidate, interviewSettingId) => {
  const stepInterviews = (candidate?.interviews ?? [])
    .filter(interview => !interview.supersededByInterviewId)
    .filter((interview) => Number(interview?.interviewSettingId) === Number(interviewSettingId));

  if (!stepInterviews.length) return undefined;

  const liveInterviews = stepInterviews.filter((interview) => interview?.status !== STATUS_CANCELLED);

  return [...(liveInterviews.length ? liveInterviews : stepInterviews)]
    .sort((a, b) => new Date(b?.created_at ?? 0) - new Date(a?.created_at ?? 0))[0];
};

export const assignNextStep = (application, state, pushToState) => {
  if (application.withdrawn_at) {
    let revisedApplication = {
      ...application,
      ...application?.isHeadless && {undo: true},
      currentStep: {name: "Applicants"},
      nextStep: null,
      prevStep: null,
    };

    if (pushToState) return state.steps.match[1].candidates.push(revisedApplication);
    else return revisedApplication;
  }

  // Counts do not identify a stage when earlier interviews were cancelled or repeated.
  // The backend progresses from the furthest active setting in this same ordered list.
  const activeSettingIds = new Set((Array.isArray(application.interviews) ? application.interviews : [])
    .filter(interview => interview.status !== STATUS_CANCELLED)
    .map(interview => String(interview.interviewSettingId)));
  const activeInterviewIndexes = state.steps.connect
    .map((step, index) => activeSettingIds.has(String(step.id)) ? index : -1)
    .filter(index => index !== -1);
  const currentInterviewIndex = resolveInterviewStepIndex(application, state.steps.connect);
  const lastInterviewStep = currentInterviewIndex === -1
    ? {name: "Applicants"}
    : {name: "Interview", index: currentInterviewIndex};

  if (application.hired_at) {
    let revisedApplication = {
      ...application,
      ...application?.isHeadless && {undo: true},
      currentStep: {name: "Hired"},
      prevStep: state.backgroundCheckRequired ? {name: "Background Check"}
        : state.showOfferLetters ? {name: "Offer Letter"} : lastInterviewStep
    };

    if (pushToState) {
      let hireIndex = 0;
      if (state.backgroundCheckRequired) hireIndex++;
      if (state.showOfferLetters) hireIndex++;
      return state.steps.hire[hireIndex].candidates.push(revisedApplication);
    }
    else return revisedApplication;
  }

  if (state.backgroundCheckRequired) {
    if (application.background_check_started_at) {
      let revisedApplication = {
        ...application,
        ...application?.isHeadless && {undo: true},
        currentStep: {name: "Background Check"},
        nextStep: {name: "Hired"},
        prevStep: state.showOfferLetters ? {name: "Offer Letter"} : lastInterviewStep
      };

      if (pushToState) {
        let backgroundCheckIndex = 0;
        if (state.showOfferLetters) backgroundCheckIndex++;
        return state.steps.hire[backgroundCheckIndex].candidates.push(revisedApplication);
      }
      else return revisedApplication;
    }
  }

  if (state.showOfferLetters) {
    if (application.offer_letter_started_at) {
      let revisedApplication = {
        ...application,
        ...application?.isHeadless && {undo: true},
        currentStep: {name: "Offer Letter"},
        nextStep: {name: state.backgroundCheckRequired ? "Background Check" : "Hired"},
        prevStep: lastInterviewStep
      };

      if (pushToState) return state.steps.hire[0].candidates.push(revisedApplication);
      else return revisedApplication;
    }
  }

  if (currentInterviewIndex >= 0 && state.steps.connect[currentInterviewIndex]) {
    let nextStep, prevStep;

    const nextInterviewStep = state.steps.connect[currentInterviewIndex + 1];

    if (nextInterviewStep) {
      nextStep = {
        name: "Interview",
        index: currentInterviewIndex + 1,
        schedulingMethod: nextInterviewStep?.schedulingMethod
      };
    } else {
      nextStep = {name: state.showOfferLetters ? "Offer Letter" : state.backgroundCheckRequired ? "Background Check" : "Hired"};
    }

    if (!Array.isArray(application.interviews)) {
      prevStep = currentInterviewIndex === 0 ? {name: "Applicants"} : {name: "Interview", index: currentInterviewIndex - 1};
    } else if (activeInterviewIndexes.length === 1) {
      prevStep = {name: "Applicants"};
    } else {
      prevStep = {name: "Interview", index: activeInterviewIndexes.at(-2)};
    }

    let revisedApplication = {
      ...application,
      ...application?.isHeadless && {
        undo: true
      },
      currentStep: {name: "Interview", index: currentInterviewIndex},
      nextStep: nextStep,
      prevStep: prevStep
    };


    if (pushToState) return state.steps.connect[currentInterviewIndex].candidates.push(revisedApplication);
    else return revisedApplication;
  }

  // Get the scheduling method for the first interview step
  const firstInterviewStep = state.steps.connect[0];
  const schedulingMethod = firstInterviewStep?.schedulingMethod;

  let revisedApplication = {
    ...application,
    currentStep: {name: "Applicants"},
    nextStep: {
      name: "Interview",
      index: 0,
      schedulingMethod: schedulingMethod
    },
    prevStep: null,
  };

  if (pushToState) state.steps.match[1].candidates.push(revisedApplication);
  else return revisedApplication;
};

export const handleMapInvites = (state, invites, pushToState) => {
  for (let i = 0; i < invites.length; i++) {
    invites[i].currentStep = {name: "Invitation Pending"};
  }

  if (pushToState) state.steps.match[0].candidates.push(...invites);
  else return invites;
};


// Sort candidates in a step
export const sortColumnHelper = (step, sortSettings) => {
  switch (sortSettings) {
  case "progress-high":
    step.candidates.sort((a, b) => {
      const aProgress = getCurrentStepProgressIndex(a, step?.id);
      const bProgress = getCurrentStepProgressIndex(b, step?.id);
      return bProgress - aProgress;
    });
    toast.info("Column sorted by progress, most to least");
    break;
  case "progress-low":
    step.candidates.sort((a, b) => {
      const aProgress = getCurrentStepProgressIndex(a, step?.id);
      const bProgress = getCurrentStepProgressIndex(b, step?.id);
      return aProgress - bProgress;
    });
    toast.info("Column sorted by progress, least to most");
    break;
  case "score-high":
    step.candidates.sort((a, b) => {
      const aScore = a.averageInterviewScore || 0;
      const bScore = b.averageInterviewScore || 0;
      return bScore - aScore;
    });
    toast.info("Column sorted by score, highest to lowest");
    break;
  case "score-low":
    step.candidates.sort((a, b) => {
      const aScore = a.averageInterviewScore || 0;
      const bScore = b.averageInterviewScore || 0;
      return aScore - bScore;
    });
    toast.info("Column sorted by score, lowest to highest");
    break;
  case "alphabetic":
    step.candidates.sort((a, b) => {
      const aName = ((a.candidateFirstName || a.firstName || "") + (a.candidateLastName || a.lastName || "")).toLowerCase().replace(/[^a-z]+/g, "");
      const bName = ((b.candidateFirstName || b.firstName || "") + (b.candidateLastName || b.lastName || "")).toLowerCase().replace(/[^a-z]+/g, "");
      return aName.localeCompare(bName);
    });
    toast.info("Column sorted alphabetically");
    break;
  case "alphabetic-reverse":
    step.candidates.sort((a, b) => {
      const aName = ((a.candidateFirstName || a.firstName || "") + (a.candidateLastName || a.lastName || "")).toLowerCase().replace(/[^a-z]+/g, "");
      const bName = ((b.candidateFirstName || b.firstName || "") + (b.candidateLastName || b.lastName || "")).toLowerCase().replace(/[^a-z]+/g, "");
      return bName.localeCompare(aName);
    });
    toast.info("Column sorted reverse-alphabetically");
    break;
  }
};


// Functionality for dragging a candidate from one step to another
export const dragStartHelper = (card, state) => {
  state.draggingCard = card;

  setTimeout(() => {
    state.isDragging = true;
    state.draggingPrevStep = card.undo ? card.prevStep : null;
    state.draggingNextStep = card.nextStep;
    state.draggingCurrentStep = card.currentStep;
  }, 10); // Wait for the drag image to be set, must be >= 10
};

export const getEducationAndWorkString = (candidateProfile) => {
  const currentEducation = getCurrentEducation(candidateProfile.education);
  const mostRecentJob = getMostRecentJob(candidateProfile.careerPassport);

  if (!currentEducation && !mostRecentJob) return "No Work or School Provided";

  let subtitle = "";

  if (currentEducation) {
    subtitle += `Studying ${currentEducation?.primary} at ${currentEducation?.title}`;
  }

  if (mostRecentJob) {
    if (subtitle) subtitle += " | ";
    subtitle += `${mostRecentJob?.primary} at ${mostRecentJob?.title}`;
  }

  return subtitle;
};

export const getCurrentStepFunnel = (candidate, interviewSteps) => {
  if (candidate.currentStep?.name === "Hired") {
    const onboardingEmailCopy = `Onboarding steps (if any) sent ${formatDateLong(candidate.hired_at)}`;
    const onboardingEmailIcon = candidate.onboardingViewCount > 0 ? EnvelopeOpenIconOutline : EnvelopeIcon;
    const viewCountCopy = candidate.onboardingViewCount ? `Onboarding steps viewed ${candidate.onboardingViewCount} ${pluralize("time", candidate.onboardingViewCount)}` : "Onboarding not viewed yet";
    const onboardingSubmittedCopy = candidate.onboarding_submitted_at ?( "Onboarding content submitted " + formatDateLong(candidate.onboarding_submitted_at)) : "Candidate not submitted onboarding yet";
    const onboardingCompletedCopy = candidate.onboarding_completed_at ? `Onboarding content completed ${formatDateLong(candidate.onboarding_completed_at)}` : "Onboarding not marked completed yet";

    return [
      {content: onboardingEmailCopy, icon: onboardingEmailIcon, completed: true},
      {content: viewCountCopy, icon: EyeIcon, completed: !!candidate.onboardingViewCount, iconCount: candidate.onboardingViewCount},
      {content: onboardingSubmittedCopy, icon: PaperAirplaneIcon, completed: !!candidate.onboarding_submitted_at},
      {content: onboardingCompletedCopy, icon: CheckCircleIcon, completed: !!candidate.onboarding_completed_at},
    ];
  }

  if (candidate.currentStep?.name === "Background Check") {
    if (!candidate.backgroundChecks) candidate.backgroundChecks = [];
    candidate.backgroundChecks?.sort((a, b) => (b.sent_at ? new Date(b.created_at) : 0) - (a.created_at ? new Date(a.created_at) : 0));

    const hasACheck = !!candidate.backgroundChecks?.length;
    const hasACheckCopy = hasACheck ? "Background Check Created" : "No Background Checks Yet";


    const checkOrderSubmitted = candidate.backgroundChecks?.find(b => b.order_guid);
    const checkOrderSubmittedCopy = checkOrderSubmitted ? "Sent to Candidate" : "Not Sent to Candidate";
    const checkOrderSubmittedIcon = checkOrderSubmitted?.viewCount === 0 ? EnvelopeOpenIconOutline : EnvelopeIcon;

    const checkCompleted = candidate.backgroundChecks?.find(bg => BACKGROUND_CHECK_STATUS_COMPLETED_GOOD.includes(bg.status) || BACKGROUND_CHECK_STATUS_ERROR.includes(bg.status));
    const checkCompletedCopy = checkCompleted ? `Background Check Finished With Status ${checkCompleted.status}` : "No Results Yet";
    const checkCompletedIcon = BACKGROUND_CHECK_STATUS_ERROR.includes(checkCompleted?.status) ? ExclamationCircleIcon : InformationCircleIcon;

    const checkProcessing = candidate.backgroundChecks?.find(bg => BACKGROUND_CHECK_STATUS_IN_PROGRESS.includes(bg.status));
    const checkProcessingCopy = checkCompleted ? "Check Completed" : checkProcessing ? `In Progress: ${checkProcessing.status}` : "Not In Progress Yet";

    return [
      {content: hasACheckCopy, icon: Cog6ToothIcon, completed: hasACheck},
      {content: checkOrderSubmittedCopy, icon: checkOrderSubmittedIcon, completed: !!checkOrderSubmitted, iconCount: candidate.backgroundChecks?.length},
      {content: checkProcessingCopy, icon: UserPlusIcon, completed: !!checkProcessing || !!checkCompleted},
      {content: checkCompletedCopy, icon: checkCompletedIcon, completed: !!checkCompleted},
    ];
  }

  if (candidate.currentStep?.name === "Offer Letter" || candidate.currentStep?.name === "Offer") {
    const offerCreated = !!candidate.offerLetters?.length;
    const offerCreatedCopy = offerCreated ? `Offer Started ${formatDateLong(candidate.offer_letter_started_at)}` : "Offer Letter Not Started Yet";

    candidate.offerLetters?.sort((a, b) => (b.sent_at ? new Date(b.sent_at) : 0) - (a.sent_at ? new Date(a.sent_at) : 0));
    const offerSent = candidate.offerLetters?.find(offer => offer.sent_at);
    const offerSentCopy = offerSent ? `Most Recent Offer Sent ${formatDateLong(offerSent.sent_at)} • Viewed ${offerSent?.viewCount || 0} ${pluralize("time", offerSent?.viewCount || 0)}` : "Offer Not Sent Yet";
    const offerSentIcon = offerSent?.viewCount === 0 ? EnvelopeOpenIconOutline : EnvelopeIcon;

    const offerSigned = candidate.offerLetters?.find(offer => offer.signed_at);
    const offerSignedCopy = offerSigned ? `Offer Accepted ${formatDateLong(offerSigned.signed_at)}` : "Offer Not Accepted Yet";


    return [
      {content: offerCreatedCopy, icon: DocumentTextIcon, completed: offerCreated},
      {content: offerSentCopy, icon: offerSentIcon, completed: !!offerSent?.sent_at, iconCount: candidate.offerLetters?.length},
      {content: offerSignedCopy, icon: PencilIcon, completed: !!offerSigned?.signed_at},
    ];
  }

  if (candidate.currentStep?.name === "Interview") {
    const interviewStepSettings = interviewSteps?.[candidate.currentStep.index];
    const interview = findStepInterview(candidate, interviewStepSettings?.id);

    let interviewIcon;
    if (interviewStepSettings?.location === LOCATION_IN_PERSON) interviewIcon = BuildingOfficeIcon;
    else if (interviewStepSettings?.location === LOCATION_PHONE) interviewIcon = PhoneIcon;
    else interviewIcon = VideoCameraIcon;

    const completesAt = interview?.created_at ? new Date(interview.created_at) : new Date();
    completesAt.setMinutes(completesAt.getMinutes() + 5);

    const notifiedIcon = interview?.views ? EnvelopeOpenIconOutline : EnvelopeIcon;
    let notifiedCopy = interview?.views ? `Scheduling link sent ${formatDateLong(completesAt)} – Viewed ${interview?.views} ${pluralize("time", interview?.views)}` : `Scheduling link sent ${formatDateLong(completesAt)} – Not viewed yet`;
    if (!interview?.created_at) notifiedCopy = "Scheduling link notifications are delayed 5 minutes";

    const interviewScheduled = !!interview?.start_time || interview?.status === STATUS_COMPLETED;
    const scheduledCopy = interviewScheduled ? `Scheduled for ${formatDateLong(interview.start_time)} • ${interview?.durationMinutes || "0"} minutes` : "Not Scheduled Yet";

    const interviewCompleted = interview?.status === STATUS_COMPLETED;
    let interviewCopy = interviewCompleted ? `Completed ${formatDateLong(interview.end_time)}` : `Interview Status: ${interview?.status || "Not Completed"}`;

    const interviewScored = !!interview?.avgScore;
    const avgScoreRounded = interviewScored ? Math.round(interview.avgScore * 10) / 10 : 0;
    const scoredCopy = interviewScored ? `Interviewers rated this interview ${avgScoreRounded} out 10` : "The interviewers need to give their scores still";

    let notificationRetries = 0;

    if (!interviewScheduled && !interviewCompleted && !interviewScored && interview?.notificationCount) {
      notificationRetries = interview.notificationCount - 1;
    }

    const candidateWasNoShow = !!interview?.candidate_no_show_reported_at;
    if (candidateWasNoShow) interviewCopy = `Candidate was marked a no-show ${formatDateLong(interview.candidate_no_show_reported_at)}`;

    const funnelItems = [];

    // Add the notified icon first
    funnelItems.push(
      {icon: notifiedIcon, completed: interview?.created_at ? true : completesAt > new Date(), content: notifiedCopy, iconCount: notificationRetries}
    );

    // Add pre-interview task icon if tasks exist
    if (interview?.taskAssignments && interview.taskAssignments.length > 0) {
      const preInterviewTasks = interview.taskAssignments.filter(task => task.timing === "pre_interview");
      if (preInterviewTasks.length > 0) {
        // Separate candidate and company tasks
        const candidateTasks = preInterviewTasks.filter(task => task.assigneeType === "App\\Models\\Candidate");
        const companyTasks = preInterviewTasks.filter(task => 
          task.assigneeType === "App\\Models\\TeamMember" || task.assigneeType === "App\\Models\\InterviewSetting"
        );

        const candidateCompleted = candidateTasks.length > 0 && candidateTasks.every(task => task.status === "completed");
        const companyCompleted = companyTasks.length > 0 && companyTasks.every(task => task.status === "completed");
        const allPreTasksCompleted = preInterviewTasks.every(task => task.status === "completed");

        // Build content with separate lines for candidate and company tasks
        const contentLines = [];
        if (candidateTasks.length > 0) {
          const candidateText = candidateCompleted
            ? `Candidate: ${candidateTasks.length} task${candidateTasks.length > 1 ? "s" : ""} completed`
            : `Candidate: ${candidateTasks.length} task${candidateTasks.length > 1 ? "s" : ""} pending`;
          contentLines.push(candidateText);
        }
        if (companyTasks.length > 0) {
          const companyText = companyCompleted
            ? `Company: ${companyTasks.length} task${companyTasks.length > 1 ? "s" : ""} completed`
            : `Company: ${companyTasks.length} task${companyTasks.length > 1 ? "s" : ""} pending`;
          contentLines.push(companyText);
        }

        funnelItems.push({
          icon: ClipboardDocumentCheckIcon,
          completed: allPreTasksCompleted,
          content: contentLines.join("<br>"),
          isTask: true,
          taskAssignments: preInterviewTasks,
        });
      }
    }

    // Add the remaining standard interview funnel items
    funnelItems.push(
      {icon: CalendarDaysIcon, completed: interviewScheduled, content: scheduledCopy},
      {icon: interviewIcon, completed: interviewCompleted, content: interviewCopy, xMark: candidateWasNoShow},
      {icon: StarIcon, completed: interviewScored, content: scoredCopy},
    );

    // Add post-interview task icon if tasks exist
    if (interview?.taskAssignments && interview.taskAssignments.length > 0) {
      const postInterviewTasks = interview.taskAssignments.filter(task => task.timing === "post_interview");
      if (postInterviewTasks.length > 0) {
        // Separate candidate and company tasks
        const candidateTasks = postInterviewTasks.filter(task => task.assigneeType === "App\\Models\\Candidate");
        const companyTasks = postInterviewTasks.filter(task => 
          task.assigneeType === "App\\Models\\TeamMember" || task.assigneeType === "App\\Models\\InterviewSetting"
        );

        const candidateCompleted = candidateTasks.length > 0 && candidateTasks.every(task => task.status === "completed");
        const companyCompleted = companyTasks.length > 0 && companyTasks.every(task => task.status === "completed");
        const allPostTasksCompleted = postInterviewTasks.every(task => task.status === "completed");

        // Build content with separate lines for candidate and company tasks
        const contentLines = [];
        if (candidateTasks.length > 0) {
          const candidateText = candidateCompleted
            ? `Candidate: ${candidateTasks.length} task${candidateTasks.length > 1 ? "s" : ""} completed`
            : `Candidate: ${candidateTasks.length} task${candidateTasks.length > 1 ? "s" : ""} pending`;
          contentLines.push(candidateText);
        }
        if (companyTasks.length > 0) {
          const companyText = companyCompleted
            ? `Company: ${companyTasks.length} task${companyTasks.length > 1 ? "s" : ""} completed`
            : `Company: ${companyTasks.length} task${companyTasks.length > 1 ? "s" : ""} pending`;
          contentLines.push(companyText);
        }

        funnelItems.push({
          icon: ClipboardDocumentCheckIcon,
          completed: allPostTasksCompleted,
          content: contentLines.join("<br>"),
          isTask: true,
          taskAssignments: postInterviewTasks,
        });
      }
    }

    return funnelItems;
  }

  if (candidate.currentStep?.name === "Applicants") {
    let reviewCopy = "Not Reviewed Yet";
    if (candidate.reviewed_at) reviewCopy = `Reviewed ${formatDateLong(candidate.reviewed_at)}`;

    return [
      {content: `Applied ${formatDateLong(candidate?.created_at)}`, icon: DocumentTextIcon, completed: true},
      {content: reviewCopy, icon: EyeIcon, completed: !!candidate?.reviewed_at},
    ];
  }

  if (candidate.currentStep?.name === "Invitation Pending") {
    const inviteIcon = candidate?.views ? EnvelopeOpenIconOutline : EnvelopeIcon;
    const inviteCopy = candidate?.views ? `Invite Sent – Viewed ${candidate?.views} ${pluralize("time", candidate?.views)}` : "Invite Sent – Not viewed yet";

    let retries = 0;
    if (candidate.notificationCount) retries = candidate.notificationCount - 1;

    return [
      {content: inviteCopy, icon: inviteIcon, completed: true, iconCount: retries},
      {content: "Pending Candidate Response", icon: CheckCircleIcon},
    ];
  }

  return [
    {content: "Error Loading Current Progress", icon: ExclamationTriangleIcon},
  ];
};

const getCurrentStepProgressIndex = (candidate, stepId) => {
  if (candidate?.invitationId) return candidate?.views ? 1 : 0;

  let interview;

  switch (candidate?.currentStep?.name) {
  case "Applicants":
    return candidate?.reviewed_at ? 1 : 0;
  case "Interview":
    interview = findStepInterview(candidate, stepId);
    if (interview?.avgScore) return 5;
    if (interview?.status === STATUS_COMPLETED) return 4;
    if (interview?.start_time) return 3;
    if (interview?.views) return 2;
    if (interview?.created_at) return 1;
    return 0;
  case "Offer":
  case "Offer Letter":
    if (candidate.offerLetters?.find(offer => offer.signed_at)) return 4;
    if (candidate.offerLetters?.find(offer => offer.viewCount)) return 3;
    if (candidate.offerLetters?.length) return 2;
    if (candidate.offer_letter_started_at) return 1;
    return 0;
  case "Background Check":
    return candidate.background_check_started_at ? 1 : 0;
  case "Hired":
    return 1;
  }
};

// Structured evidence lets presentation reuse the warning rules without parsing tooltip copy.
export const getCurrentStepWarningFacts = (candidate, interviewSteps, {now = new Date(), environment = import.meta.env.VITE_ENV} = {}) => {
  if ([ENV_DEMO, ENV_STAGING].includes(environment) || candidate?.withdrawn_at || candidate?.disqualified_at) return false;

  const isPendingInvitation = candidate?.invitationId;
  let interviewStepSettings, interview, mostRecentOffer, offerLetters, diffInDays;
  const warning = (source, anchor, message) => ({source, anchor, days: Math.round(diffInDays), message});

  if (isPendingInvitation) {
    diffInDays = differenceBetweenDates(candidate.created_at, now);
    if (diffInDays >= 14) return warning("invitation.created_at", candidate.created_at, `Invitation pending ${Math.round(diffInDays)}+ days`);
    return false;
  }

  switch (candidate?.currentStep?.name) {
  case "Applicants":
    diffInDays = differenceBetweenDates(candidate?.created_at, now);
    if (diffInDays >= 6) return warning("application.created_at", candidate?.created_at, `Applied ${Math.round(diffInDays)}+ days ago`);
    return false;
  case "Interview":
    interviewStepSettings = interviewSteps?.[candidate.currentStep.index];
    interview = findStepInterview(candidate, interviewStepSettings?.id);

    if (interview?.status === STATUS_COMPLETED) {
      diffInDays = differenceBetweenDates(interview?.end_time, now);
      if (diffInDays >= 6) return warning("interview.end_time", interview?.end_time, `Interviewed ${Math.round(diffInDays)}+ days ago`);
      return false;
    }

    if (interview?.start_time) return false;

    diffInDays = differenceBetweenDates(interview?.created_at, now);
    if (diffInDays >= 6) return warning("interview.created_at", interview?.created_at, `Schedule link sent ${Math.round(diffInDays)}+ days ago`);
    return false;
  case "Offer":
  case "Offer Letter":
    offerLetters = candidate?.offerLetters;

    if (!offerLetters?.length) {
      diffInDays = differenceBetweenDates(candidate?.offer_letter_started_at, now);
      if (diffInDays >= 14) return warning("application.offer_letter_started_at", candidate?.offer_letter_started_at, `No offer sent for ${Math.round(diffInDays)}+ days`);
      return false;
    }

    if (candidate?.offer_letter_accepted_at) return false;

    mostRecentOffer = offerLetters.reduce((prev, current) => (prev.created_at > current.created_at) ? prev : current);
    diffInDays = differenceBetweenDates(mostRecentOffer?.created_at, now);
    if (diffInDays >= 8) return warning("offer.created_at", mostRecentOffer?.created_at, `Most recent offer ${Math.round(diffInDays)}+ days ago`);

    return false;
  case "Background Check":
  case "Hired":
  default:
    return false;
  }
};

export const getCurrentStepWarning = (candidate, interviewSteps) => getCurrentStepWarningFacts(candidate, interviewSteps)?.message || false;
